import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { MFA_LEVELS, MFA_LEVEL_LABEL, type MfaLevel } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { passkeysHere, readConfig } from '../config.ts';
import { PasskeysPanel } from '../passkeys/PasskeysPanel.tsx';
import { loadMembers } from '../users/api.ts';
import {
  listFactors,
  loadAssurance,
  loadMemberFactors,
  loadTenantLevel,
  removeFactor,
  resetTwoStep,
  saveTenantLevel,
  type Assurance,
  type Factor,
} from './api.ts';
import { TwoStepCode, TwoStepEnrol } from './TwoStep.tsx';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const day = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

export interface SecurityScreenProps {
  readonly sb: SupabaseClient;
  /** The workspace's tenant when the person is a tenant admin there: the organisation setting and people. */
  readonly adminTenantId?: string;
  /** Tell the app the session's level changed (a code entered), so its gate looks again. */
  readonly onSessionChanged?: () => void;
  /** The page's title: "My sign-in" from the top bar (C7, decision 844), "Security" in Braynewave's console. */
  readonly title?: string;
  /** A client's own report emails (F07), shown below the two-step sign-in. */
  readonly reportEmails?: ReactElement;
  /** Show "Passkeys" (decision 804); by default when this address is one of the build's passkey origins. */
  readonly passkeys?: boolean;
}

/** "My sign-in" (the top bar; "Security" in Braynewave's console): your own two-step sign-in, and for tenant admins the organisation's setting and resets. */
export function SecurityScreen({
  sb,
  adminTenantId,
  onSessionChanged,
  title = 'My sign-in',
  reportEmails,
  passkeys = passkeysHere(readConfig().auth),
}: SecurityScreenProps): ReactElement {
  const [assurance, setAssurance] = useState<Assurance | null>(null);
  const [factors, setFactors] = useState<Factor[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'idle' | 'enrol' | 'code'>('idle');
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<Factor | null>(null);

  const reload = useCallback(() => {
    setError(null);
    Promise.all([loadAssurance(sb), listFactors(sb)])
      .then(([a, f]) => {
        setAssurance(a);
        setFactors(f);
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb]);
  useEffect(reload, [reload]);

  const changed = (): void => {
    setMode('idle');
    reload();
    onSessionChanged?.();
  };

  return (
    <section className="page">
      <PageHeader
        title={title}
        subtitle="Two-step sign-in: a code from an authenticator app on your phone after the email link, Google or Microsoft."
      />
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!error && (!assurance || !factors) && <Loading what="your sign-in" />}
      {assurance && factors && (
        <div className="panel stack">
          <h2 className="heading">Your two-step sign-in</h2>
          <p className="body">
            {factors.length === 0
              ? 'Off. You sign in with one step.'
              : `On: ${factors.length === 1 ? '1 authenticator app' : `${factors.length} authenticator apps`}.`}{' '}
            {assurance.aal === 'aal2'
              ? 'You entered a code in this session.'
              : factors.length > 0
                ? 'You have not entered a code in this session yet.'
                : ''}
          </p>
          {factors.length > 0 && (
            <ul className="stack">
              {factors.map((f) => (
                <li key={f.id} className="row">
                  <span className="body">
                    {f.name} <span className="caption muted">· added {day(f.createdAt)}</span>
                  </span>
                  <Button
                    variant="quiet"
                    aria-label={`Remove ${f.name}`}
                    onClick={() => (assurance.aal === 'aal2' ? setConfirmRemove(f) : setMode('code'))}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {confirmRemove && (
            <div className="panel confirm" role="dialog" aria-labelledby="remove-app-title">
              <p id="remove-app-title" className="body">
                Remove {confirmRemove.name}? Its codes stop working straight away.
                {factors.length === 1 ? ' You will sign in with one step until you set up another app.' : ''}
              </p>
              <div className="row">
                <Button
                  className="btn--danger"
                  onClick={() => {
                    const f = confirmRemove;
                    setConfirmRemove(null);
                    removeFactor(sb, f.id)
                      .then(() => setNotice({ ok: true, text: `${f.name} removed.` }))
                      .catch((e: unknown) => setNotice({ ok: false, text: message(e) }))
                      .finally(changed);
                  }}
                >
                  Remove app
                </Button>
                <Button variant="quiet" onClick={() => setConfirmRemove(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {mode === 'idle' && (
            <div className="row">
              {factors.length > 0 && assurance.aal !== 'aal2' ? (
                <Button variant="primary" onClick={() => setMode('code')}>
                  Enter a code
                </Button>
              ) : (
                <Button
                  variant={factors.length === 0 ? 'primary' : 'secondary'}
                  onClick={() => setMode('enrol')}
                >
                  {factors.length === 0 ? 'Set up an authenticator app' : 'Add a backup app'}
                </Button>
              )}
            </div>
          )}
          {mode === 'code' && (
            <div className="stack">
              <p className="body">
                Enter a code from your authenticator app to change your two-step sign-in.
              </p>
              <TwoStepCode sb={sb} onDone={changed} />
              <Button variant="quiet" onClick={() => setMode('idle')}>
                Cancel
              </Button>
            </div>
          )}
          {mode === 'enrol' && (
            <TwoStepEnrol
              sb={sb}
              onDone={() => {
                setNotice({
                  ok: true,
                  text: 'Two-step sign-in is on. Keep a backup app in case you lose this phone.',
                });
                changed();
              }}
              onCancel={() => setMode('idle')}
            />
          )}
          <p className="caption muted">
            Lost your phone? Sign in on another device with a backup app if you added one. Otherwise ask your
            tenant admin (or Braynewave) to reset your two-step sign-in, then sign in and set up the new
            phone.
          </p>
        </div>
      )}
      {passkeys && assurance && factors && (
        <PasskeysPanel
          sb={sb}
          needsCode={factors.length > 0 && assurance.aal !== 'aal2'}
          onNeedCode={() => setMode('code')}
        />
      )}
      {assurance && <SignOutEverywhere sb={sb} />}
      {adminTenantId && assurance && (
        <OrganisationSecurity sb={sb} tenantId={adminTenantId} twoStep={assurance.aal === 'aal2'} />
      )}
      {reportEmails}
    </section>
  );
}

/**
 * "Sign out everywhere" (docs/specs/cloud/sign-in-security.md §6): ends every session of this sign-in, in every
 * browser, Teslyn Field and Teslyn Mobile, this one included. For a lost laptop or phone, or a sign-in used on a shared
 * computer. "Sign out" in the top bar ends this browser's session only.
 */
export function SignOutEverywhere({ sb }: { readonly sb: SupabaseClient }): ReactElement {
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="panel stack" aria-labelledby="signout-all-title">
      <h2 id="signout-all-title" className="heading">
        Signed in elsewhere
      </h2>
      <p className="body">
        “Sign out” in the top bar signs you out of this browser only. If a laptop or phone is lost, or you
        signed in on someone else’s computer, sign out everywhere: every browser, Teslyn Field and Teslyn
        Mobile then ask you to sign in again.
      </p>
      {error && (
        <p className="notice notice--fail" role="alert">
          {error}
        </p>
      )}
      {asking ? (
        <div className="panel confirm" role="dialog" aria-labelledby="signout-all-confirm">
          <p id="signout-all-confirm" className="body">
            Sign out everywhere, this browser included? Work not yet synced on a laptop stays on that laptop
            and syncs after you sign in there again.
          </p>
          <div className="row">
            <Button
              className="btn--danger"
              onClick={() => {
                setAsking(false);
                void sb.auth
                  .signOut({ scope: 'global' })
                  .then(({ error: e }) => e && setError(`Could not sign out everywhere: ${e.message}`));
              }}
            >
              Sign out everywhere
            </Button>
            <Button variant="quiet" onClick={() => setAsking(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="row">
          <Button onClick={() => setAsking(true)}>Sign out everywhere…</Button>
        </div>
      )}
    </div>
  );
}

/** Tenant admins: who must use two-step sign-in, who has it on, and the reset for a lost phone. */
export function OrganisationSecurity({
  sb,
  tenantId,
  twoStep,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  /** This session entered a code: needed to change the setting or reset someone. */
  readonly twoStep: boolean;
}): ReactElement {
  const [level, setLevel] = useState<MfaLevel | null>(null);
  const [draft, setDraft] = useState<MfaLevel>('off');
  const [people, setPeople] = useState<
    { readonly userId: string; readonly name: string; readonly what: string; readonly apps: number }[] | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [resetting, setResetting] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    setError(null);
    Promise.all([loadTenantLevel(sb, tenantId), loadMembers(sb, tenantId), loadMemberFactors(sb, tenantId)])
      .then(([l, m, f]) => {
        setLevel(l);
        setDraft(l);
        const seen = new Set<string>();
        const rows: { userId: string; name: string; what: string; apps: number }[] = [];
        for (const s of m.staff) {
          seen.add(s.userId);
          rows.push({
            userId: s.userId,
            name: s.displayName || s.email || 'Unnamed user',
            what: 'Staff',
            apps: f.get(s.userId) ?? 0,
          });
        }
        for (const c of m.clients) {
          if (seen.has(c.userId)) continue;
          seen.add(c.userId);
          rows.push({
            userId: c.userId,
            name: c.displayName || c.email || 'Unnamed user',
            what: c.clientOrgName,
            apps: f.get(c.userId) ?? 0,
          });
        }
        setPeople(rows);
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb, tenantId]);
  useEffect(reload, [reload]);

  async function save(): Promise<void> {
    setBusy(true);
    try {
      await saveTenantLevel(sb, tenantId, draft);
      setNotice({ ok: true, text: `Saved: ${MFA_LEVEL_LABEL[draft]}.` });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel stack">
      <h2 className="heading">Your organisation</h2>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!error && (!level || !people) && <Loading what="your organisation's sign-in setting" />}
      {level && people && (
        <>
          <fieldset className="stack">
            <legend className="label muted">Who must use two-step sign-in</legend>
            {MFA_LEVELS.map((l) => (
              <label key={l} className="row body">
                <input type="radio" name="mfa-level" checked={draft === l} onChange={() => setDraft(l)} />
                {MFA_LEVEL_LABEL[l]}
              </label>
            ))}
          </fieldset>
          {!twoStep && (
            <p className="caption muted">
              To change this, set up two-step sign-in for yourself and enter a code in this session.
            </p>
          )}
          <div className="row">
            <Button
              variant="primary"
              onClick={() => void save()}
              disabled={draft === level || !twoStep}
              busy={busy}
            >
              Save
            </Button>
          </div>
          <p className="caption muted">
            People who must use it set up an authenticator app the next time they sign in, in Teslyn Cloud or
            Teslyn Field. Client users are never required to.
          </p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="label">Name</th>
                  <th className="label">Staff or client</th>
                  <th className="label">Two-step sign-in</th>
                  <th className="label">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <tr key={p.userId}>
                    <td>{p.name}</td>
                    <td>{p.what}</td>
                    <td>{p.apps === 0 ? 'Off' : p.apps === 1 ? 'On (1 app)' : `On (${p.apps} apps)`}</td>
                    <td className="table__actions">
                      {p.apps > 0 && (
                        <Button
                          variant="quiet"
                          aria-label={`Reset two-step sign-in of ${p.name}`}
                          disabled={!twoStep}
                          onClick={() => {
                            setNote('');
                            setResetting(p.userId);
                          }}
                        >
                          Reset
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {resetting && (
            <div className="panel confirm stack" role="dialog" aria-labelledby="reset-title">
              <p id="reset-title" className="body">
                Reset two-step sign-in of {people.find((p) => p.userId === resetting)?.name}? Their
                authenticator apps stop working. They sign in with one step and set up a new app. Do this only
                when you are sure it is them asking, for example on a phone call.
              </p>
              <label className="label muted" htmlFor="reset-note">
                Note for the audit log
              </label>
              <input
                spellCheck
                id="reset-note"
                className="input"
                value={note}
                maxLength={300}
                onChange={(e) => setNote(e.target.value)}
              />
              <div className="row">
                <Button
                  className="btn--danger"
                  onClick={() => {
                    const target = resetting;
                    setResetting(null);
                    resetTwoStep(sb, target, note)
                      .then((n) =>
                        setNotice({
                          ok: true,
                          text: `Two-step sign-in reset (${n} app${n === 1 ? '' : 's'} removed).`,
                        }),
                      )
                      .catch((e: unknown) => setNotice({ ok: false, text: message(e) }))
                      .finally(reload);
                  }}
                >
                  Reset two-step sign-in
                </Button>
                <Button variant="quiet" onClick={() => setResetting(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
